import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { getInitData } from '@shared/lib/telegram';

export interface MoneyDto {
  amount: string;
  label: string;
}

/**
 * One API slice for the whole app; entity slices extend it with injectEndpoints
 * so each business domain owns its own queries without a second cache.
 */
export const baseApi = createApi({
  reducerPath: 'api',
  tagTypes: [
    'User',
    'Product',
    'Order',
    'Deposit',
    'Admin',
    'AdminSummary',
    'AdminAnalytics',
    'AdminDeposit',
    'AdminProduct',
    'ManualProduct',
    'Delivery',
    'CanvaOrder',
    'AdminOrder',
    'AdminUser',
    'Ban',
    'Discount',
    'Maintenance',
    'Feedback',
    'AdminFeedback',
    'ResellerAccount',
    'ResellerProduct',
    'ResellerOrder',
    'AdminReseller',
    'AdminResellerProduct',
    'Backup',
    'Giveaway',
    'AdminGiveaway',
    'Category',
    'AdminCategory',
    'PinnedNumber',
    'SmsPricing',
    'NumbersVisibility',
    'NumberService',
    'ShopService',
    'AdminShopService',
    'Celebration',
    'Referral',
    'AdminReferral',
    'FlashDraw',
    'AdminFlashDraw',
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: '/api',
    prepareHeaders: (headers) => {
      // Re-read on every request: Telegram can refresh initData mid-session.
      const initData = getInitData();
      if (initData) headers.set('Authorization', `tma ${initData}`);
      return headers;
    },
  }),
  endpoints: () => ({}),
});

/** Pulls a readable message out of an RTK Query error shape. */
export function apiErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = (error as { data?: unknown }).data;
    if (typeof data === 'object' && data !== null && 'message' in data) {
      const message = (data as { message?: unknown }).message;
      if (typeof message === 'string') return message;
    }
  }

  return fallback;
}

/** Returns the stable server error code from an RTK Query error, when present. */
export function apiErrorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null || !('data' in error)) return null;
  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null || !('error' in data)) return null;
  const code = (data as { error?: unknown }).error;
  return typeof code === 'string' ? code : null;
}
